/* ============================================================================
   WFY cart stock reconciler — fix (b), v6.
   Keeps a sold-out / over-quantity cart line from freezing the Pickeasy drawer,
   and explains (with a centred note) whenever the cart auto-adjusts a line for stock.

   THE POINT OF THIS FILE (the customer-facing requirement): a customer puts 5 of a meal
   in their cart; while they keep shopping someone else buys the stock down; the next time
   they touch their cart, their quantity is corrected to what is actually available and
   they are TOLD, rather than the cart silently changing under them.

   WHY THIS IS SMALL: the theme's OWN updateItemQty (custom-async.js:5953) already
   handles the customer's own stepper edits. This file covers only the paths
   updateItemQty never sees, all caused by Pickeasy re-stamping delivery dates on
   EVERY line on EVERY cart change (POST /cart/change.js?app=pickeasy):

     1) FREEZE — a FULLY sold-out line makes Pickeasy's re-stamp 422 ("…already
        sold out."), Pickeasy stalls, the drawer locks. We read the product name
        from that 422 and remove JUST that line with a DOWNWARD /cart/update.js
        {key:0} — which per the Shopify Ajax docs never validates quantity on an
        existing line, so it can never 422 (unlike change.js). Then re-render
        (re-running Pickeasy's stamp on the now-valid cart, un-stalling it) + note.

     2) SILENT COLLATERAL REDUCE — a cart action (e.g. removing another item) makes
        Pickeasy re-stamp and Shopify CLAMP a *different* over-stock line down to
        available (HTTP 200, no message). The theme.liquid interceptor fires a
        coalesced settle-pass on that 200 re-stamp; we note the reduce, GUARDED so
        we never re-message a line the customer just edited.
        VERIFIED WORKING on a clean cart, 17 Aug 2026 (reduce + note both correct).

     3) SILENT DROP (added v6) — when a meal sells out COMPLETELY, Shopify/Pickeasy
        removes the line by itself on the next cart write, with no message. Path (2)
        cannot see this: it walks the lines still IN the cart, and this one is gone.
        v6 compares the previous per-VARIANT rollup against the current cart and notes
        anything that disappeared. Purely observational — it writes nothing and deletes
        nothing. Matched by VARIANT, not line key, because a key is variant + hash of
        the line's properties and Pickeasy's delivery stamp changes that hash; and
        suppressed when the customer removed the line themselves, which needs the bin
        (an <a class="remove">, not a <button>) to be tracked — see stampEdit().

   OWNERSHIP IS KEY-BASED (v5): the cart stepper's data-line is a POSITION that
   renumbers when a line is removed, so an edit-stamp keyed on position stops
   matching once a sold-out line is dropped — which mislabels the customer's own
   edit as a stock reduce. We instead map the stepper's data-line to the stable
   line KEY (via the last cart order we saw) and stamp ownership on the key.

   TRUTH SOURCE: /cart.js + the sold-out product name in the live 422 body. We do
   NOT read /products/{handle}.js — its inventory_quantity is CDN-stale.

   The only cart WRITE is the {key:0} removal of a genuinely sold-out line (or, on
   the rare parse-miss fallback, a one-shot bounded probe). The reduce case writes
   NOTHING. Bundler and CheckPop are untouched. No edit to core custom-async.js.
   ============================================================================ */
(function () {
  'use strict';
  if (window.wfyReconcileCart) return; // load once

  var running = false, pendingCall = false;
  var pendingSoldOut = [];             // queued raw 422 texts still to process
  var lastQty = null;                  // { key: quantity } last seen — null until first read
  var lastOrder = null;                // [key, …] in cart order — maps a stepper's data-line to a stable key
  var lastVariants = null;             // { variant_id: {qty, title} } — see vanishedChanges()
  var keyVariant = {};                 // line KEY -> variant_id, so a click can be attributed to a variant
  var userEditedAt = {};              // line KEY -> ms of the customer's OWN stepper edit
  var userTouchedVariantAt = {};      // VARIANT -> ms of the customer's own edit/removal (survives a key change)
  var userTouchedTitleAt = {};        // normalised TITLE -> ms — position-proof ownership read from the clicked line's DOM; immune to the rapid-removal stale-position race
  var USER_EDIT_TTL = 6000;           // matches the theme's own deferred-toast window
  var selfRerender = false, selfRerenderT;

  /* ---------- tiny helpers ---------- */
  function now() { return Date.now(); }

  function jget(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) {
        return { status: r.status, ok: r.ok, body: j };
      });
    }).catch(function () { return { status: 0, ok: false, body: null }; });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (m) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[m];
    });
  }
  function titleOf(it) { return it.product_title || it.title || 'This meal'; }
  function qtyMap(items) { var m = {}; if (items) items.forEach(function (it) { m[it.key] = it.quantity; }); return m; }
  // Roll the cart up BY VARIANT. Deliberately not by line key: a key is
  // variant_id + a hash of the line's properties, and Pickeasy stamps delivery
  // properties onto every line — so a changed delivery slot renumbers the key and a
  // key-based "it's gone" test would report a removal that never happened.
  function variantMap(items) {
    var m = {};
    (items || []).forEach(function (it) {
      if (!m[it.variant_id]) m[it.variant_id] = { qty: 0, title: titleOf(it) };
      m[it.variant_id].qty += it.quantity;
    });
    return m;
  }
  // Record the quantities, the ORDER (so a stepper's data-line maps to a stable key),
  // the per-variant rollup, and key→variant for click attribution.
  function snapshot(items) {
    lastQty = qtyMap(items);
    lastOrder = (items || []).map(function (it) { return it.key; });
    lastVariants = variantMap(items);
    keyVariant = {};
    (items || []).forEach(function (it) { keyVariant[it.key] = it.variant_id; });
  }

  /* ---------- (3) VANISHED: a whole variant dropped out of the cart ----------
     When a meal sells out completely, Shopify/Pickeasy removes the line on the next cart
     write — silently, and WITHOUT us doing anything. The collateral-reduce check below
     can never see it, because that iterates the lines still IN the cart and this one is
     gone. So we compare the previous per-variant rollup against the current one.
     Purely observational — no cart write, nothing deleted here.
     Suppressed when the customer removed it themselves (bin click / their own stepper
     edit), or we would tell them a meal sold out every time they emptied a line. */
  function vanishedChanges(items, t) {
    var out = [];
    if (!lastVariants) return out;                 // no baseline yet — say nothing
    var present = {};
    (items || []).forEach(function (it) { present[it.variant_id] = true; });
    Object.keys(lastVariants).forEach(function (vid) {
      if (present[vid]) return;
      if (userTouchedVariantAt[vid] && (t - userTouchedVariantAt[vid] < USER_EDIT_TTL)) return;
      var vTitle = lastVariants[vid].title;
      // Position-proof guard: the customer clicked-remove on THIS meal (its title was read straight
      // from that line's DOM at click time), even if rapid removals left the position→variant map stale.
      if (vTitle && userTouchedTitleAt[norm(vTitle)] && (t - userTouchedTitleAt[norm(vTitle)] < USER_EDIT_TTL)) return;
      out.push({ title: vTitle, removed: true });
    });
    return out;
  }

  // Normalise a product name for tolerant comparison (fold dashes + quotes + case + spacing).
  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[–—]/g, '-').replace(/["'’“”]/g, '').replace(/\s+/g, ' ').trim();
  }

  // Pull the sold-out product name from a Shopify 422 body/text. Primary, specific
  // match only — no loose "first quoted token" fallback (which could delete a valid
  // line, since {key:0} never pushes back). A parse-miss is handled by probeSoldOut.
  function parseSoldOut(text) {
    if (!text) return null;
    var s = typeof text === 'string' ? text : JSON.stringify(text);
    if (!/sold\s*out|no\s+longer\s+available|not\s+available|unavailable/i.test(s)) return null;
    var m = s.match(/product\s+['"“](.+?)['"”]\s+is\s+already\s+sold\s*out/i);
    return (m && m[1]) ? m[1] : null;
  }

  /* ---------- re-render + Pickeasy un-stall (reuses the theme's own machinery) ---------- */
  function rerender(didRemove) {
    selfRerender = true; // trigger 1 swallows the wfy:cart-dom-updated our redraw emits
    clearTimeout(selfRerenderT);
    selfRerenderT = setTimeout(function () { selfRerender = false; }, 1500); // never poison the next real event
    try {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        window.ajaxCart.load(false, false, false, true); // re-fetch side-cart section (server-rendered from live cart)
      }
    } catch (e) {}
    // Only nudge Pickeasy when we actually removed a line (a 422 stalled it). On a
    // reduce-only pass there was no 422/stall, so the nudge would just churn.
    if (didRemove) {
      try {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var x = new XMLHttpRequest();
        x.open('POST', root + 'cart/update.js');
        x.setRequestHeader('Content-Type', 'application/json');
        x.send(JSON.stringify({}));
      } catch (e) {}
    }
  }

  /* ---------- the note — centred card, white bg, navy border; tap to dismiss ---------- */
  var toastEl, toastT, toastMsgs = [];
  function hideToast() { if (toastEl) toastEl.classList.remove('is-on'); toastMsgs = []; clearTimeout(toastT); }
  function ensureToast() {
    if (toastEl) return;
    var s = document.createElement('style');
    s.textContent =
      '#wfy-stock-toast{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%) scale(.96);' +
      'width:min(92vw,400px);box-sizing:border-box;background:#ffffff;color:#12133d;' +
      'border:2px solid #12133d;border-top:4px solid #257DAD;' +
      "font-family:'Poppins',system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      'font-size:15px;line-height:1.5;padding:22px 26px 18px;box-shadow:0 22px 60px rgba(18,19,61,.30);' +
      'opacity:0;pointer-events:none;transition:opacity .2s ease,transform .2s ease;z-index:2147483001;text-align:center;}' +
      '#wfy-stock-toast.is-on{opacity:1;transform:translate(-50%,-50%) scale(1);pointer-events:auto;cursor:pointer;}' +
      '#wfy-stock-toast strong{font-weight:700;}' +
      '#wfy-stock-toast .wfy-toast-hint{display:block;margin-top:14px;font-size:12px;opacity:.5;}';
    document.head.appendChild(s);
    toastEl = document.createElement('div');
    toastEl.id = 'wfy-stock-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.addEventListener('click', hideToast);
    document.body.appendChild(toastEl);
  }
  function msgFor(c) {
    return c.removed
      ? '<strong>' + esc(c.title) + '</strong> has sold out, so we’ve removed it from your cart.'
      : '<strong>' + esc(c.title) + '</strong> now has only ' + c.to + ' available — we’ve updated your cart.';
  }
  // Accumulate distinct messages while the note is visible, so a removal note is never
  // buried by a later reduce note (and vice-versa) when they land in separate passes.
  function notify(changes) {
    if (!changes || !changes.length) return;
    ensureToast();
    changes.forEach(function (c) { var m = msgFor(c); if (toastMsgs.indexOf(m) === -1) toastMsgs.push(m); });
    toastEl.innerHTML = toastMsgs.join('<br>') + '<span class="wfy-toast-hint">Tap to dismiss</span>';
    void toastEl.offsetWidth; // reflow so the transition runs on first show
    toastEl.classList.add('is-on');
    clearTimeout(toastT);
    toastT = setTimeout(hideToast, 9000);
  }

  /* ---------- bounded parse-miss fallback: find sold-out lines by probing ---------- */
  // Only runs when a Pickeasy 422 arrived but no product name could be parsed — rare.
  function probeSoldOut(items) {
    var found = [];
    return items.reduce(function (p, it) {
      return p.then(function () {
        return postJSON('/cart/change.js', { id: it.key, quantity: it.quantity }).then(function (res) {
          if (res.status === 422) {
            var msg = (res.body && (res.body.description || res.body.message)) || '';
            if (/sold\s*out|not\s+available|unavailable/i.test(msg)) found.push({ key: it.key, title: titleOf(it) });
          }
        });
      });
    }, Promise.resolve()).then(function () { return found; });
  }

  function applyRemoval(removeKeys, changes, fallbackItems) {
    var updates = {};
    removeKeys.forEach(function (k) { updates[k] = 0; });
    return postJSON('/cart/update.js', { updates: updates }).then(function () {
      return jget('/cart.js');
    }).then(function (c2) {
      snapshot((c2 && c2.items) || fallbackItems);
      return { changed: true, changes: changes, didRemove: true };
    });
  }

  /* ---------- the reconcile ----------
     soldOutText: optional live 422 body/text naming a sold-out product to remove.
     Undefined for a plain settle pass (trigger 1, the 200-clamp hook, or checkout). */
  function reconcile(soldOutText) {
    if (soldOutText != null) pendingSoldOut.push(soldOutText);
    if (running) { pendingCall = true; return Promise.resolve({ changed: false }); }
    running = true;
    var t = now();

    // Drain every queued 422 text → names to remove + whether any sold-out 422 is pending.
    var texts = pendingSoldOut.splice(0, pendingSoldOut.length);
    var names = {}, sawSoldOut422 = texts.length > 0, parsedAny = false;
    texts.forEach(function (tx) { var n = parseSoldOut(tx); if (n) { names[norm(n)] = true; parsedAny = true; } });

    return jget('/cart.js').then(function (cart) {
      if (!cart || !cart.items) { return { changed: false }; }   // fetch failed → KEEP baseline
      if (!cart.items.length) {
        // Cart is now empty. Still worth a note if their LAST line sold out from under
        // them — but vanishedChanges() stays silent when they emptied it themselves.
        var goneAll = vanishedChanges([], t);
        snapshot([]);
        return goneAll.length ? { changed: true, changes: goneAll, didRemove: false } : { changed: false };
      }

      // (1a) FREEZE candidates: lines whose title matches a parsed sold-out name.
      var named = cart.items.filter(function (it) {
        var a = norm(it.product_title || ''), b = norm(it.title || '');
        for (var nm in names) { if (Object.prototype.hasOwnProperty.call(names, nm) && (a === nm || b === nm)) return true; }
        return false;
      });
      // The 422 names only the PRODUCT. If the matches span more than one variant we can't
      // tell which sold out — probe to confirm before removing, so we never delete a valid
      // sibling variant. Single-variant (the WFY norm) removes directly.
      var vids = {}; named.forEach(function (it) { vids[it.variant_id] = true; });
      var resolveRemovals = !named.length ? Promise.resolve([])
        : (Object.keys(vids).length > 1) ? probeSoldOut(named)
        : Promise.resolve(named.map(function (it) { return { key: it.key, title: titleOf(it) }; }));

      return resolveRemovals.then(function (rem) {
        var removeSet = {}, removeKeys = [], changes = [];
        rem.forEach(function (r) {
          if (!removeSet[r.key]) { removeSet[r.key] = true; removeKeys.push(r.key); changes.push({ title: r.title, removed: true }); }
        });

        // (2) COLLATERAL REDUCE: a line dropped below what it last held, not via the
        //     customer's own recent stepper edit (theme owns those) and not a removal.
        if (lastQty) {
          cart.items.forEach(function (it) {
            if (removeSet[it.key]) return;
            var prev = lastQty[it.key];
            if (prev != null && it.quantity < prev) {
              var edited = userEditedAt[it.key] && (t - userEditedAt[it.key] < USER_EDIT_TTL);
              if (!edited) changes.push({ title: titleOf(it), to: it.quantity });
            }
          });
        }

        // (3) VANISHED: a variant Shopify/Pickeasy dropped outright because it sold out.
        //     Lines WE are about to remove are still in cart.items on this pass, so they
        //     can't double-report; and notify() dedupes identical messages anyway.
        vanishedChanges(cart.items, t).forEach(function (c) { changes.push(c); });

        // (1b) FREEZE fallback: a sold-out 422 with no parseable name and nothing matched
        //      by title → probe to find the actually-sold-out line(s). Bounded, rare.
        if (sawSoldOut422 && !parsedAny && !removeKeys.length) {
          return probeSoldOut(cart.items).then(function (found) {
            if (!found.length) {
              snapshot(cart.items);
              return changes.length ? { changed: true, changes: changes, didRemove: false } : { changed: false };
            }
            found.forEach(function (f) {
              if (!removeSet[f.key]) { removeSet[f.key] = true; removeKeys.push(f.key); changes.push({ title: f.title, removed: true }); }
            });
            return applyRemoval(removeKeys, changes, cart.items);
          });
        }

        if (!changes.length) { snapshot(cart.items); return { changed: false }; }
        if (!removeKeys.length) { snapshot(cart.items); return { changed: true, changes: changes, didRemove: false }; }
        return applyRemoval(removeKeys, changes, cart.items);
      });
    }).then(function (res) {
      running = false;
      if (res && res.changed) { rerender(res.didRemove); notify(res.changes); }
      if (pendingCall) { pendingCall = false; setTimeout(function () { reconcile(); }, 350); }
      return res;
    }).catch(function () { running = false; return { changed: false }; });
  }

  // Exposed for the Pickeasy XHR interceptor (theme.liquid): pass the 422 responseText
  // on a sold-out, or call with no arg on a 200 re-stamp settle pass.
  window.wfyReconcileCart = reconcile;

  /* ---------- seed the snapshot once, so the first collateral reduce is detectable ---------- */
  jget('/cart.js').then(function (cart) {
    if (lastQty === null) snapshot(cart && cart.items);
  });

  /* ---------- record the customer's OWN stepper edits (key-based, no async race) ----------
     Cart steppers are input[name="updates[]"] with data-line (1-based POSITION). We map
     that position to the stable line KEY via the last cart order we saw, and stamp the
     key — so the reduce-note never re-messages a line the customer changed, even after a
     sold-out line is removed and the positions renumber. Records intent only; never writes. */
  function lineFromEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return 0;
    // The drawer's Remove control is an <a class="remove remove-from-cart-link"> carrying
    // its OWN data-line (side-cart.liquid:227) — check it first.
    var rem = t.closest('a.remove[data-line], .remove-from-cart-link[data-line]');
    if (rem && rem.dataset.line) return parseInt(rem.dataset.line, 10);
    var el = t.closest('input[name="updates[]"]');
    if (el && el.dataset && el.dataset.line) return parseInt(el.dataset.line, 10);
    var box = t.closest('.qty, .quantity, [data-quantity], li');
    if (box) {
      var inp = box.querySelector && box.querySelector('input[name="updates[]"]');
      if (inp && inp.dataset && inp.dataset.line) return parseInt(inp.dataset.line, 10);
    }
    return 0;
  }
  // Read the meal's title straight from the clicked line's DOM (.cols h2 a in side-cart.liquid).
  // This is ALWAYS the current line the customer clicked, unlike the position→variant map, which
  // lags during rapid removals (positions renumber immediately; lastOrder only refreshes at
  // reconcile-end). This is what makes ownership survive fast successive removals.
  function titleFromEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return '';
    var li = t.closest('li');
    if (!li) return '';
    var a = li.querySelector('.cols h2 a, .cols a[href*="/products/"]');
    var txt = a ? a.textContent : '';
    return txt ? txt.trim() : '';
  }
  function stampEdit(e) {
    // Title stamp FIRST (position-proof): protects the exact meal the customer acted on, even if a
    // rapid-removal renumber makes the position→variant map below resolve to the wrong line.
    var liTitle = titleFromEvent(e);
    if (liTitle) userTouchedTitleAt[norm(liTitle)] = now();

    var ln = lineFromEvent(e);
    if (!ln || !lastOrder) return;
    var key = lastOrder[ln - 1];
    if (!key) return;
    userEditedAt[key] = now();
    // Stamp the VARIANT too. The key disappears with the line, so a key-only stamp can't
    // protect a removal — without this, every manual bin click would pop up a false
    // "this meal sold out" note.
    var vid = keyVariant[key];
    if (vid) userTouchedVariantAt[vid] = now();
  }
  document.addEventListener('change', stampEdit, true);
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest &&
      e.target.closest('button, a.remove, .remove-from-cart-link, .qtyplus, .qtyminus, [data-qty-plus], [data-qty-minus]');
    if (btn) stampEdit(e);
  }, true);

  /* ---------- trigger 1: on the customer's own cart change (debounced, silent unless changed) ---------- */
  var deb;
  document.addEventListener('wfy:cart-dom-updated', function () {
    if (selfRerender) { selfRerender = false; clearTimeout(selfRerenderT); return; } // ignore our own redraw
    clearTimeout(deb);
    deb = setTimeout(function () { reconcile(); }, 350);
  }, false);

  /* ---------- trigger 2: checkout click — option A (stop, show, require a re-click) ----------
     Fails OPEN: if the reconcile hangs (this store sees CDN throttling), a 1200ms race
     lets checkout proceed rather than dead-ending the button. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[name="checkout"]');
    if (!btn) return;
    if (btn.__wfyStockOk) { btn.__wfyStockOk = false; return; } // already reconciled — let it through
    e.preventDefault();
    e.stopImmediatePropagation();
    Promise.race([
      reconcile(),
      new Promise(function (r) { setTimeout(function () { r({ changed: false }); }, 1200); })
    ]).then(function (res) {
      if (res && res.changed) return;   // something changed — note + redraw fired; customer re-clicks
      btn.__wfyStockOk = true;
      btn.click();                      // re-dispatch → passes us → theme's own checkout gate runs
    });
  }, true);

})();
